import {
  buildMediaDayImagenEditorialOutputStem,
  buildMediaDayOutputStem,
  type MediaDaySlotId,
} from "./slots.js";

export type VoxisVerificationStage =
  | "sourceJpg"
  | "cutout"
  | "cropped"
  | "teamcomp"
  | "editorialSource"
  | "editorialEdited";

export const VOXIS_MEDIA_DAY_ROOT =
  "PHOTO BACKUP/01 Broadcast/02 Media Day/02 Media Day Work Folder";

const voxisGameFolders = [
  ["01 MLBB Wildcard", ["mlbb wildcard", "ml bb", "mobile legends wildcard"]],
  ["02 Valorant", ["valorant"]],
  ["03 Apex Legends", ["apex legends", "apex"]],
  ["04 Dota 2", ["dota 2", "dota2"]],
  ["05 FATAL FURY", ["fatal fury"]],
  ["06 League of Legends", ["league of legends", "lol"]],
  ["07 MLBB MWI", ["mlbb mwi", "mwi"]],
  ["08 Free Fire", ["free fire"]],
  ["09 PUBG PC", ["pubg pc", "pubg"]],
  ["10 Teamfight Tactics", ["teamfight tactics", "tft"]],
  ["11 EA FC 26", ["ea fc 26", "ea fc"]],
  ["12 MLBB MSC", ["mlbb msc", "msc"]],
  ["13 Overwatch 2", ["overwatch 2", "overwatch"]],
  ["14 COD Warzone", ["cod warzone", "call of duty warzone", "warzone"]],
  ["15 Street Fighter 6", ["street fighter 6", "street fighter"]],
  ["16 Honor of Kings", ["honor of kings", "hok"]],
  ["17 Rainbow Six Siege", ["rainbow six siege", "r6"]],
  ["18 PUBG MOBILE", ["pubg mobile"]],
  ["19 TEKKEN 8", ["tekken 8", "tekken"]],
  ["20 COD BO7", ["cod bo7", "black ops 7", "call of duty black ops 7"]],
  ["21 Chess", ["chess"]],
  ["22 Rocket League", ["rocket league"]],
  ["23 Counter Strike 2", ["counter strike 2", "counter-strike 2", "cs2"]],
  ["24 Fortnite", ["fortnite"]],
  ["25 CROSSFIRE", ["crossfire"]],
  ["26 Trackmania", ["trackmania"]],
] as const;

const normalizeGame = (value: string) =>
  value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const voxisGameFolder = (name: string, tagToken?: string | null) => {
  const candidates = new Set([normalizeGame(name), normalizeGame(tagToken ?? "")]);
  return voxisGameFolders.find(([, aliases]) =>
    aliases.some((alias) => candidates.has(normalizeGame(alias))),
  )?.[0];
};

export const voxisParticipantFolderName = ({
  participantMode,
  teamName,
  personSlug,
}: {
  participantMode: "team" | "individual";
  teamName: string;
  personSlug?: string;
}) => (participantMode === "individual" && personSlug ? personSlug : teamName.trim());

export const buildVoxisFolderPrefix = ({
  gameFolder,
  teamName,
  stage,
  date,
}: {
  gameFolder: string;
  teamName: string;
  stage: VoxisVerificationStage;
  date?: string;
}) =>
  [
    VOXIS_MEDIA_DAY_ROOT,
    gameFolder,
    stage === "sourceJpg"
      ? "01 Cutout JPG"
      : stage === "cutout"
        ? "02 Cutout HighRes PNG"
        : stage === "cropped"
          ? "04 Cropped 4k Player PNG"
          : stage === "teamcomp"
            ? "06 Cropped 4k Teamcomp PNG"
            : stage === "editorialSource"
              ? "07 Editorial JPG"
              : "08 Editorial Editted HighRes JPG",
    date &&
    (stage === "sourceJpg" ||
      stage === "cutout" ||
      stage === "editorialSource" ||
      stage === "editorialEdited")
      ? date
      : null,
    stage === "sourceJpg" ||
    stage === "cutout" ||
    stage === "editorialSource" ||
    stage === "editorialEdited"
      ? teamName
      : null,
  ]
    .filter(Boolean)
    .join("/");

export type VoxisExpectedFile = { expected: string; alternatives: string[] };

const editorialSourceExtensions = [
  ".JPG",
  ".JPEG",
  ".jpg",
  ".jpeg",
  ".CR3",
  ".cr3",
  ".CR2",
  ".cr2",
  ".NEF",
  ".nef",
  ".ARW",
  ".arw",
  ".RAF",
  ".raf",
];
const editorialEditedExtensions = [".JPG", ".JPEG", ".jpg", ".jpeg"];

export const generateVoxisEditorialManifest = ({
  stage,
  date,
  gameCode,
  teamCode,
  personSlug,
  teamAsset = false,
  solo = false,
}: {
  stage: "editorialSource" | "editorialEdited";
  date: string;
  gameCode: string;
  teamCode: string;
  personSlug?: string;
  teamAsset?: boolean;
  solo?: boolean;
}): VoxisExpectedFile[] => {
  const slots = teamAsset
    ? (["edi_team_pose1"] as const)
    : (["edi_pose1", "edi_pose2", "edi_pose3"] as const);
  const extensions =
    stage === "editorialSource" ? editorialSourceExtensions : editorialEditedExtensions;
  return slots.flatMap((slotId) => {
    const stem = buildMediaDayImagenEditorialOutputStem({
      date,
      gameToken: gameCode,
      teamToken: solo ? "x" : teamCode,
      playerToken: personSlug ?? null,
      slotId,
    });
    return stem
      ? [
          {
            expected: `${stem}.JPG`,
            alternatives: extensions.map((extension) => `${stem}${extension}`),
          },
        ]
      : [];
  });
};

export type VoxisManifestPerson = {
  gameCode: string;
  teamCode: string;
  personSlug: string;
  role?: string | null;
  isCaptain?: boolean;
};

const normalize = (value: string) => value.trim().toLowerCase();
const playerCutVariants = ["f", "pose1", "pose2", "pose3", "pose4", "pose5", "pose6"];
const captainVariants = ["f", "key", "pose1", "pose2", "pose3", "pose4", "pose5", "pose6"];

export const voxisPersonVariants = ({
  role,
  isCaptain,
  substitutesFull = false,
}: {
  role?: string | null;
  isCaptain?: boolean;
  substitutesFull?: boolean;
}) => {
  if (isCaptain) return captainVariants;
  const normalizedRole = normalize(role ?? "");
  const isPlayer = normalizedRole === "player" || normalizedRole === "starter";
  const isSubstitute = normalizedRole === "substitute" || normalizedRole === "substitute player";
  return isPlayer || (substitutesFull && isSubstitute) ? playerCutVariants : ["f"];
};

export const buildVoxisPlayerCutFilename = ({
  gameCode,
  teamCode,
  personSlug,
  variant,
}: VoxisManifestPerson & { variant: string }) => {
  const slotId = (variant === "f" ? "cut_f" : `cut_${variant}`) as MediaDaySlotId;
  const stem = buildMediaDayOutputStem({
    gameToken: gameCode,
    teamToken: teamCode,
    playerToken: personSlug,
    slotId,
  });
  return `${stem ?? `${gameCode}_${teamCode}_${personSlug}_cut_${variant}`}.png`;
};

export const generateVoxisVerificationManifest = ({
  stage,
  players,
  team,
  substitutesFull = false,
}: {
  stage: VoxisVerificationStage;
  players: VoxisManifestPerson[];
  team?: { gameCode: string; teamCode: string } | null;
  substitutesFull?: boolean;
}) => {
  if (stage === "teamcomp") {
    return team ? [`${team.gameCode}_${team.teamCode}_cut_teamcomp.png`] : [];
  }

  return players.flatMap((player) =>
    voxisPersonVariants({ role: player.role, isCaptain: player.isCaptain, substitutesFull }).map(
      (variant) => {
        const filename = buildVoxisPlayerCutFilename({ ...player, variant });
        return stage === "sourceJpg" ? filename.replace(/\.png$/, ".jpg") : filename;
      },
    ),
  );
};
